/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * This file contains the component that provides context for the online patient
 * management system.
 * ---------------------------------------------------------------------------------
 */

/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */

/**
 * Required to use React components.
 */
import * as React from 'react';

/*
 * Used to style components
 */
import { AlertTitle, Box, Button, Chip, Dialog, DialogContent, Paper, styled, Typography } from '@mui/material';

/**
 * Used for the basic page layout.
 */
import {
    CrfCondition,
    Form,
    FormSubmitButton,
    GroupedField,
    GroupedFields,
    IAdditionalLink,
    ICrfConditionParameters,
    IFormValidationError,
    ILookup,
    ILookupItem,
    InstitutionContext,
    IPatientSummaryUpdateListener,
    OnlinePatientManagementContext,
    PageLayout,
    PatientBreadcrumbs,
    PatientContext,
    PatientSummary,
    PatientSummaryManager,
    RouteLoading,
    Select,
    Text,
    useAsyncAction,
    usePatientSave,
    useSnackbar,
    ValidationErrorType
} from '@ngt/opms';



/*
 * Used to type patient state.
 */
import PatientStatusInformation from '@/components/patientSummary/PatientStatusInformation';
import usePatientQueries from '@/hooks/data/usePatientQueries';
import usePatientSummaryEventActions from '@/hooks/page/usePatientSummaryEventActions';
import { ConditionMode, IFormSubmit, IFormValidate, ValidateOn } from '@ngt/forms-core';
import { usePermissionsByIds } from '@ngt/opms-bctapi';
import { ResponseStatus } from '@servicestack/client';
import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LookupIneligibleReasonType, Patient, PatientStateType, Permission, QueryStatusType, SetPatientStatus } from '../api/dtos';
import PatientDetails from '@/components/common/PatientDetails';

/*
 * ----------------------------------------------------------------------------------
 * Interface
 * ----------------------------------------------------------------------------------
 */

interface IPatientSummaryProps {
}

interface IFormDialogProps {
    open: boolean,
    setOpen: React.Dispatch<React.SetStateAction<boolean>>
    form: React.ReactNode,
    loading: boolean,
    width?: "xs" | "sm" | "md" | "lg" | "xl"
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const StyledContainer = styled(Paper)(({theme}) => ({
    padding: theme.spacing(3)
}));

const StyledTableContainer = styled(Box)(({theme}) => ({
    padding: theme.spacing(4, 0, 0, 0)
}));


const StyledDialogTitle = styled(Typography)(({ theme }) => ({
    padding: theme.spacing(3),
    fontSize: '1.25rem',
    fontWeight: 500
}))

const StyledButtonGroup = styled(Box)(({ theme }) => ({
    margin: theme.spacing(2, 0, 0, 0),
    gap: theme.spacing(2),
    justifyContent: 'flex-end',
    display: 'flex'
}))

const INELIGIBILITY_REASONS = [
    'Inclusion Criteria - Participants aged ≥18 years.',
    'Inclusion Criteria - Histologically confirmed diagnosis of metastatic/advanced unresectable HER2-positive or HER2-low breast cancer.',
    'Inclusion Criteria - Planned to start treatment with T-DXd.',
    'Inclusion Criteria - Life expectancy of at least 4 months.',
    'Exclusion Criteria - Current active reversible causes of decreased food intake.',
    'Exclusion Criteria - Receiving tube feedings or any kind of parenteral nutrition.',
    'Exclusion Criteria - Ongoing cachexia attributable to other reasons unrelated to cancer or cancer treatment.',
    'Exclusion Criteria - Current adherence to a calorie-restricted diet with the intention of weight loss.',
    'Participant declined (no reason given or not interested in trial).',
    'Other'
]

const ineligibleReasonOtherSpecify = ({ formState }: ICrfConditionParameters<IMarkAsIneligibleForm>) => formState?.value?.ineligibleReason === LookupIneligibleReasonType.OtherSpecify;

export const FormDialog: FC<IFormDialogProps> = ({
    open,
    setOpen,
    form,
    loading,
    width
}) => {
    return (
        <Dialog open={open} onClose={() => setOpen(false)} aria-labelledby="form-dialog" maxWidth={width} fullWidth scroll="body">
            <DialogContent style={{ padding: '0' }}>
                {!loading ? form : <RouteLoading />}
            </DialogContent>
        </Dialog>
    );
}


interface IMarkAsIneligibleForm {
    ineligibleReason?: number,
    ineligibleReasonOtherSpecify?: string,
}

const PatientSummaryPage: React.FunctionComponent<IPatientSummaryProps> = () => {
    const navigate = useNavigate();

    const { data: institution } = useContext(InstitutionContext);
    const { data: untypedPatient, refresh: patientRefresh } = useContext(PatientContext);

    const patient = untypedPatient as Patient;
    
    const {data: patientQueries, loading: patientQueriesLoading} = usePatientQueries(patient?.id);

    const [patientStatusSaving, setPatientStatusSaving] = useState<boolean>(false);
    const [markAsIneligibleDialogOpen, setMarkAsIneligibleDialogOpen] = useState<boolean>(false);
    
    const {serviceStackClient} = useContext(OnlinePatientManagementContext);
    const { enqueueSnackbar } = useSnackbar();

    const {
        permissions: [
            canOpmsAdmin, 
            canOpmsDataManage, 
            canViewPatient,
            canUpdatePatient
        ],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.OpmsAdminister, // BCT staff + CRA/CSM
            Permission.OpmsDataManage,
            Permission.OpmsPatientView,
            Permission.OpmsPatientUpdate
        ], 
        undefined, 
        undefined, 
        institution?.id
    );
        
    const canAdministerOpms = useMemo(() => {
        return canOpmsAdmin || canOpmsDataManage;
    }, [canOpmsAdmin, canOpmsDataManage]);
    
    const managerRef = useRef<PatientSummaryManager>(undefined);

    useEffect(() => {
        if (!permissionsLoading) {
            if (!canViewPatient && !canAdministerOpms) {
                navigate(`/registration`, {replace: true});
            }
        }
    }, [canViewPatient, canAdministerOpms, permissionsLoading, navigate]);

    const [patientValid, setPatientValid] = useState(false);

    const markAsIneligibleLookups: ILookup[] = useMemo(() => {
        var ineligibleReasonLookup = {
            propertyName: 'ineligibleReason',
            items: [] as ILookupItem[]
        };

        ineligibleReasonLookup.items = INELIGIBILITY_REASONS.map((x, index) => {
            return {
                id: index+1,
                value: x
            } as ILookupItem
        });

        return [ineligibleReasonLookup];
    }, []);

    const { action: savePatient, pending: patientSaving } = useAsyncAction(
        usePatientSave(),
        useMemo(() => ({ 
            errorAccessor: (err) => err?.responseStatus as ResponseStatus
        }), [])
    );
    
    const updatePatientState = useCallback(async (patientStateId: PatientStateType) => {
        await savePatient(new Patient({ ...patient, previousPatientStateId: patient?.patientStateId, patientStateId: patientStateId }), false);
        await patientRefresh();
        if (managerRef.current) {
            await managerRef.current.load();
        }
        
    }, [patient, savePatient, managerRef]);

    const eventActionMapping = usePatientSummaryEventActions(
        patientSaving, 
        updatePatientState, 
        enqueueSnackbar, 
        serviceStackClient, 
        patient, 
        patientValid, 
        institution, 
        canAdministerOpms,
        managerRef.current
    )


    const onPatientValidChange: IPatientSummaryUpdateListener = useCallback((summaryState) => {
        setPatientValid(!!summaryState?.valid);
    }, [setPatientValid])

    const chipValue = useMemo(() => {
        if(patientQueriesLoading) {
            return undefined;
        }
        if(!patientQueries || patientQueries.length == 0) {
            return `0/${patientQueries?.length ?? 0} Open`;
        }
        const open = patientQueries.filter(x => x.status !== QueryStatusType.Closed).length;
        return `${open}/${patientQueries?.length ?? 0} Open`;
    }, [patientQueriesLoading, patientQueries])

    const additionalLinks = useMemo<IAdditionalLink[]>(() => [
        {
            title: "Queries",
            to: "queries",
            ExtraComponent: () => chipValue ? <Chip label={chipValue} /> : undefined
        }
    ], [chipValue]);
    
    const markAsIneligiblelValidate: IFormValidate<IMarkAsIneligibleForm, IFormValidationError> = useCallback(async (formState) => {

        let errors: Record<string, IFormValidationError[]> = {};

        if (formState.value?.ineligibleReason === undefined) {
            errors['ineligibleReason'] = [
                {
                    property: 'ineligibleReason',
                    code: 'I-001',
                    message: 'This field is required.',
                    detailedMessage: 'This field is required.',
                    type: ValidationErrorType.Critical
                }
            ];
        }

        if(formState.value?.ineligibleReason === LookupIneligibleReasonType.OtherSpecify && (formState.value?.ineligibleReasonOtherSpecify === undefined || formState.value.ineligibleReasonOtherSpecify.length == 0)) {
            errors['ineligibleReasonOtherSpecify'] = [
                {
                    property: 'ineligibleReasonOtherSpecify',
                    code: 'I-003',
                    message: 'This field is required.',
                    detailedMessage: 'This field is required.',
                    type: ValidationErrorType.Critical
                }
            ];
        }

        return errors;
    }, [institution]);


    const onRestoreClick = useCallback(async () => {
        try {
            await updatePatientState((patient as Patient)?.previousPatientStateId ?? PatientStateType.Baseline);

            enqueueSnackbar(
                <>
                    <AlertTitle>
                        Participant Restored
                    </AlertTitle>
                    The participant was successfully restored.
                </>,
                { variant: 'success' }
            );
        }
        catch {
            enqueueSnackbar(
                <>
                    <AlertTitle>
                        Participant Not Restored
                    </AlertTitle>
                    An error occurred while attempting to restore the participant.
                </>,
                { variant: 'critical' }
            );
        }
    }, [updatePatientState, enqueueSnackbar, patient]);
    
    const onMarkAsIneligibleSubmit: IFormSubmit<IMarkAsIneligibleForm, IFormValidationError> = useCallback(async (form) => {
        setPatientStatusSaving(true);

        let refreshPatient = false;

        try {
            const notes = `
                ${form.value?.ineligibleReason != null ? INELIGIBILITY_REASONS[form.value.ineligibleReason-1] : ""}
                ${
                    form.value?.ineligibleReasonOtherSpecify != null ? 
                        ' - ' + form.value.ineligibleReasonOtherSpecify : 
                        ''
                }
            `;

            await serviceStackClient.post(new SetPatientStatus({
                patientId: patient?.id,
                patientStateId: PatientStateType.Ineligible,
                notes: notes
            }));

            refreshPatient = true;

            setPatientStatusSaving(false);

            setMarkAsIneligibleDialogOpen(false);

            enqueueSnackbar(
                <>
                    <AlertTitle>
                    Participant Marked As Ineligible
                    </AlertTitle>
                    Participant status was successfully marked as ineligible.
                </>,
                { variant: 'success' }
            );
        }
        catch (error: any) {
            setPatientStatusSaving(false);

            enqueueSnackbar(
                <>
                    <AlertTitle>
                        Participant Not Marked As Ineligible
                    </AlertTitle>
                    {error.responseStatus?.errorCode === 'ClientViewableException' ? error.responseStatus.message : 'An error occurred while attempting to mark the participant as ineligible.'}
                </>,
                { variant: 'critical' }
            );
        }
    

        if (refreshPatient) {
            await patientRefresh();

            if (managerRef.current) {
                await managerRef.current.load();
            }
        }
    }, [serviceStackClient, enqueueSnackbar, setMarkAsIneligibleDialogOpen, setPatientStatusSaving, patientRefresh, managerRef, patient]);
    
    
    return (
        <PageLayout
            breadcrumbs={<PatientBreadcrumbs />}
            heading='Participant Summary'
        >
            <StyledContainer>
                <PatientDetails />
                <StyledTableContainer>

                    <PatientStatusInformation 
                        patient={patient}
                    />
                    
                    <Box
                        sx={{
                            mt: 2
                        }}
                    >
                        <PatientSummary 
                            managerRef={managerRef}
                            eventActions={eventActionMapping}
                            subscribe={{
                                onUpdate: onPatientValidChange,
                                subscription: {
                                    valid: true,
                                    data: true
                                }
                            }}
                            institutionCode={institution?.code}
                            additionalLinks={additionalLinks}
                            enableQueries
                        />
                        <StyledButtonGroup>   
                            {
                                (patient?.patientStateId ?? 0) == PatientStateType.Baseline && canUpdatePatient && (
                                    <>
                                        <Button
                                            loading={patientSaving}
                                            variant="contained"
                                            color="primary"
                                            onClick={() => setMarkAsIneligibleDialogOpen(true)}
                                        >
                                            Mark As Ineligible
                                        </Button>
                                        <Dialog
                                            open={markAsIneligibleDialogOpen}
                                            onClose={() => setMarkAsIneligibleDialogOpen(false)}
                                            aria-labelledby="ineligible-dialog-title"
                                            aria-describedby="ineligible-dialog-description"
                                            maxWidth="sm"
                                            fullWidth
                                        >
                                            <StyledDialogTitle
                                                color="secondary"
                                            >
                                                Mark As Ineligible
                                            </StyledDialogTitle>

                                            <Form
                                                initialValue={undefined}
                                                onSubmit={onMarkAsIneligibleSubmit}    
                                                lookups={markAsIneligibleLookups}
                                                labels={undefined}
                                                validateOn={ValidateOn.onChange} 
                                                onValidate={markAsIneligiblelValidate}              
                                            >
                                                <GroupedFields>
                                                    <GroupedField 
                                                        propertyPath="ineligibleReason"
                                                        LabelProps={{
                                                            label: "Ineligible Reason" as any
                                                        }}
                                                        component={Select}
                                                        FieldGridProps={{
                                                            size: {
                                                                xl: 12,
                                                                lg: 12,
                                                                md: 12,
                                                                sm: 12,
                                                                xs: 12
                                                            }
                                                        }}
                                                    />
                                                    <CrfCondition
                                                        mode={ConditionMode.Show}
                                                        condition={ineligibleReasonOtherSpecify}
                                                        subscription={{ value: true }}
                                                    >
                                                        <GroupedField 
                                                            propertyPath="ineligibleReasonOtherSpecify"
                                                            LabelProps={{
                                                                label: "Specify" as any
                                                            }}
                                                            component={Text}
                                                            rows={3}
                                                            FieldGridProps={{
                                                                size: {
                                                                    xl: 12,
                                                                    lg: 12,
                                                                    md: 12,
                                                                    sm: 12,
                                                                    xs: 12
                                                                }
                                                            }}
                                                        />  
                                                    </CrfCondition>  
                                                </GroupedFields>

                                                <Box
                                                    display="flex"
                                                    justifyContent="flex-end"
                                                    gap={2}
                                                    padding={2}
                                                >
                                                    <Button
                                                        color="secondary"
                                                        onClick={() => setMarkAsIneligibleDialogOpen(false)}
                                                        loading={patientStatusSaving}  
                                                    >
                                                        Cancel
                                                    </Button>

                                                    <FormSubmitButton
                                                        color="primary"   
                                                        variant="contained"     
                                                        loading={patientStatusSaving}                                    
                                                    >
                                                        Submit
                                                    </FormSubmitButton>
                                                </Box>
                                            </Form>
                                        </Dialog>
                                    </>
                                )
                            }
                            {
                                patient?.patientStateId === PatientStateType.Ineligible && canAdministerOpms && (
                                    <Button
                                        loading={patientSaving}
                                        variant="contained"
                                        color="primary"
                                        onClick={onRestoreClick}
                                    >
                                        Restore Patient
                                    </Button>
                                )
                            } 
                        </StyledButtonGroup>
                    </Box>
                </StyledTableContainer>
            </StyledContainer>
        </PageLayout>
    );
}

/*
 * ----------------------------------------------------------------------------------
 * Default Export
 * ----------------------------------------------------------------------------------
 */

export default PatientSummaryPage;
